import classes from './login-container-lyra.module.scss';

import {
  CcfBox,
  CcfButton,
  CcfNiceCxoneLogoIcon,
  CcfTextField,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import AWS from 'aws-sdk';
import { useMsal } from '@azure/msal-react';
import LoginStageTwoLyra from '../login-stage-two-lyra/login-stage-two-lyra';
import LoginResetPasswordLyra from '../login-stage-two-lyra/login-reset-password-lyra';

import {
  loginActions,
  selectLoginCredentialsState,
  selectLoginScreenState,
  selectLoginErrorState,
} from '../+state/login.slice';
import { awsS3Config } from '../../../util/common';

import { Box, InputAdornment, Link } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useState } from 'react';

/* eslint-disable-next-line */
export interface LoginContainerLyraProps {}

const S3_BUCKET = awsS3Config.s3Bucket;
const OBJECT_KEY = 'userDB/Users.json';

AWS.config.update({
  accessKeyId: awsS3Config.accessKeyId,
  secretAccessKey: awsS3Config.secretAccessKey,
  region: awsS3Config.region,
});

const s3 = new AWS.S3();

/**
 * Container component used for login (Lyra design)
 * @param props - LoginContainerLyraProps
 * @example <LoginContainerLyra />
 * @returns
 */
export function LoginContainerLyra(props: LoginContainerLyraProps) {
  const dispatch = useDispatch();
  const [translate] = useTranslator();
  const { instance } = useMsal();

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    /**
     * Function to update desktop breakpoint state on window resize
     * @example - handleResize()
     */
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const credentials = useSelector(selectLoginCredentialsState);
  const screen = useSelector(selectLoginScreenState);
  const error = useSelector(selectLoginErrorState);

  const {
    setUsername,
    setPassword,
    navigateToUsernameScreen,
    setS3RegisterdUsers,
    checkUserNameAndNavigate,
    checkUserNameAndPassword,
  } = loginActions;

  /**
   * Function to fetch the registered users list from S3
   * @example - fetchS3Object()
   */
  const fetchS3Object = async () => {
    const params = {
      Bucket: S3_BUCKET,
      Key: OBJECT_KEY,
    };

    try {
      const data = await s3.getObject(params).promise();
      const d = JSON.parse(JSON.stringify(data.Body?.toString('utf-8')));
      const userJson = JSON.parse(d);

      dispatch(setS3RegisterdUsers(userJson));
    } catch (error) {
      console.log('error', error);
    }
  };

  /**
   * Function to handle user name change
   * @param e - React.ChangeEvent<HTMLInputElement>
   * @example - onUserNameChange(e)
   */
  const onUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUsername(e.target.value));
  };

  /**
   * Function to handle password change
   * @param e - React.ChangeEvent<HTMLInputElement>
   * @example - onPasswordChange(e)
   */
  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPassword(e.target.value));
  };

  /**
   * function to handle user name submit
   * @param e -React.SyntheticEvent
   * @example - handleUsernameSubmit(e)
   */
  const handleUsernameSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await fetchS3Object();
    await dispatch(checkUserNameAndNavigate(credentials.username));
  };

  /**
   * function to handle password submit
   * @param e -React.SyntheticEvent
   * @example - handlePasswordSubmit(e)
   */
  const handlePasswordSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    dispatch(checkUserNameAndPassword(credentials));
    localStorage.setItem('credentials', JSON.stringify(credentials));
    localStorage.removeItem('contactHistoryLS');
    localStorage.removeItem('interactionHistoryLS');
  };

  /**
   * function used to navigate back to username screen
   * @example -handlePasswordScreenBackAction()
   */
  const handlePasswordScreenBackAction = () => {
    dispatch(navigateToUsernameScreen());
  };

  /**
   * Function to handle SSO login redirect for the selected provider
   * @example - handleSSOLogin()
   */
  const handleSSOLogin = () => {
    instance
      .loginRedirect({
        scopes: ['openid', 'profile', 'email'],
        redirectUri: window.location.href,
      })
      .catch((e: any) => {
        console.log(e);
      });
  };
  /**
   * Function to render the step-progress indicator dots for the current login stage; blank for the reset-password screen
   * @example renderScreenHeader()
   * @returns
   */
  const renderScreenHeader = () => {
    if (showResetPassword) {
      return null;
    }
    switch (screen.name) {
      case 'usernamelogin':
        return (
          <>
            <Box className={classes.progressIndicator} />
            <Box className={classes.circle} />
          </>
        );
      case 'passwordlogin':
        return (
          <>
            <Box className={classes.circle} />
            <Box className={classes.progressIndicator} />
          </>
        );
      default:
        return null;
    }
  };

  /**
   * Function to render the main screen content for the current login stage
   * @example renderScreen()
   * @returns
   */
  const renderScreen = () => {
    if (showResetPassword) {
      return (
        <LoginResetPasswordLyra
          onBackToSignIn={() => setShowResetPassword(false)}
        />
      );
    }
    switch (screen.name) {
      case 'usernamelogin':
        return (
          <>
            <CcfTypography className={classes.welcomeTitle}>
              {translate('welcomeHeaderText')}
            </CcfTypography>
            <CcfTypography className={classes.welcomeSubtitle}>
              {translate('signInToYourAccountText')}
            </CcfTypography>
            <form onSubmit={handleUsernameSubmit}>
              <CcfTypography className={classes.fieldLabel}>
                {translate('enterYourUsernameLabel')}
              </CcfTypography>
              <CcfTextField
                id="userName"
                placeholder={translate('userName')}
                size="small"
                variant="outlined"
                fullWidth
                value={credentials.username}
                onChange={onUserNameChange}
                helperText={error.status && error.message}
                error={error.status}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon
                        fontSize="small"
                        className={classes.inputIcon}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <CcfButton
                type="submit"
                variant="contained"
                fullWidth
                disabled={credentials.username.length === 0}
                className={
                  credentials.username.length === 0
                    ? classes.continueButtonDisabled
                    : classes.continueButton
                }
                endIcon={<ArrowForwardIcon fontSize="small" />}
              >
                {translate('continueButtonText')}
              </CcfButton>
            </form>

            <CcfBox className={classes.dividerRow}>
              <span className={classes.dividerLine} />
              <CcfTypography
                variant="inherit"
                className={classes.dividerText}
                translationKey="or"
              />
              <span className={classes.dividerLine} />
            </CcfBox>

            <CcfButton
              variant="contained"
              fullWidth
              className={
               classes.continueButton
              }
              endIcon={<ArrowForwardIcon fontSize="small" />}
              onClick={handleSSOLogin}
            >
              {translate('ssoLogin')}
            </CcfButton>
          </>
        );
      case 'passwordlogin':
        return (
          <LoginStageTwoLyra
            navigateToUsernameLoginScreen={handlePasswordScreenBackAction}
            onForgotPasswordClick={() => setShowResetPassword(true)}
            checkUserNameAndPassword={handlePasswordSubmit}
            currentusername={credentials.username}
            currentpassword={credentials.password}
            error={error}
            onPasswordChange={onPasswordChange}
          />
        );
      default:
        return <CcfTypography variant="h6">Loading...</CcfTypography>;
    }
  };

  return (
    <Box className={classes.container}>
      <Box
        className={classes.leftPanel}
        sx={{ width: isDesktop ? '360px' : '100%', flexShrink: 0 }}
      >
        <Box className={classes.card}>
          <Box className={classes.logoRow}>
            <CcfNiceCxoneLogoIcon />
          </Box>
          <Box className={classes.progressIndicatorRow}>
            {renderScreenHeader()}
          </Box>
          {renderScreen()}

          <Box className={classes.footer}>
            <CcfTypography variant="inherit" className={classes.footerText}>
              {translate('lyraFooterCopyright')}
            </CcfTypography>
            <Link className={classes.footerLink}>
              <CcfTypography variant="inherit" translationKey="footerTextLink" />
            </Link>
          </Box>
        </Box>
      </Box>

      {isDesktop && (
        <Box className={classes.rightPanel}>
          <img
            src="/assets/lyraLoginImage.png"
            alt="NiCE World"
            className={classes.rightImage}
          />
        </Box>
      )}
    </Box>
  );
}

export default LoginContainerLyra;
